import React from "react";
import ReactDOM from "react-dom/client";
import "./base/assets/styles/index.css";
import "./base/assets/icomoon/style.css";
import "./base/assets/styles/icons.css";
import CssBaseline from "@mui/material/CssBaseline";
import Router from "./base/router/Router";
import "./i18n";
import { BrowserRouter } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import { createTheme, ThemeProvider } from "@mui/material";
import { create } from "jss";
import rtl from "jss-rtl";
import { StylesProvider } from "@mui/styles";
import GlobalLoader from "./base/globalLoader/GlobalLoader";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "../src/base/auth/msalConfig"; // נתיב לקובץ ההגדרות שלך
import { StandaloneModeProvider } from "./base/contexts/StandaloneModeContext";

// QA debugging helper (runs in all environments for troubleshooting)
import "./qaDebug";

// יצירת jss עם plugin rtl
const jss = create({
  plugins: [rtl()],
});

const theme = createTheme({
  typography: {
    fontFamily: `"FbReforma"`,
  },
  palette: {
    background: {
      default: "rgba(255, 255, 255, 1);",
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            color: "rgba(117, 117, 117, 1);",
          },
        },
      },
    },
  },
  direction: "rtl",
});

const container = document.getElementById("root");
const root = ReactDOM.createRoot(container!);

// Initialize MSAL and handle redirect BEFORE rendering
msalInstance.initialize().then(async () => {
  // Check for existing accounts BEFORE handleRedirectPromise
  const accountsBefore = msalInstance.getAllAccounts();
  
  // Check URL for auth code (indicates redirect from Microsoft)
  const urlParams = new URLSearchParams(window.location.search);
  const hasCode = urlParams.has('code');
  
  // Handle redirect promise ONCE at app startup
  try {
    const response = await msalInstance.handleRedirectPromise();
    
    // Check for accounts AFTER handleRedirectPromise
    const accountsAfter = msalInstance.getAllAccounts();
    
    if (response) {
      // Store the response in sessionStorage for LoginEntra to pick up
      const dataToStore = {
        idToken: response.idToken,
        idTokenClaims: response.idTokenClaims,
        timestamp: Date.now()
      };
      sessionStorage.setItem('msalRedirectResponse', JSON.stringify(dataToStore));
    } else if (accountsAfter.length > 0 && hasCode) {
      // MSAL processed the redirect but returned null (can happen in StrictMode)
      try {
        const account = accountsAfter[0];
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: account
        });
        
        const dataToStore = {
          idToken: tokenResponse.idToken,
          idTokenClaims: tokenResponse.idTokenClaims,
          timestamp: Date.now()
        };
        sessionStorage.setItem('msalRedirectResponse', JSON.stringify(dataToStore));
        // Clear the processed flag so LoginEntra will process this
        sessionStorage.removeItem('msalLoginProcessed');
      } catch (silentError) {
        // Silent token acquisition failed
      }
    } else if (accountsAfter.length > 0) {
      // User has an account - try auto-login only if not already successfully logged in
      const hasStoredResponse = sessionStorage.getItem('msalRedirectResponse');
      const backendLoginSuccess = sessionStorage.getItem('msalBackendLoginSuccess');
      
      // Only auto-login if not already successfully logged in on backend
      if (!hasStoredResponse && !backendLoginSuccess) {
        try {
          const account = accountsAfter[0];
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ["User.Read"],
            account: account
          });
          
          const dataToStore = {
            idToken: tokenResponse.idToken,
            idTokenClaims: tokenResponse.idTokenClaims,
            timestamp: Date.now()
          };
          sessionStorage.setItem('msalRedirectResponse', JSON.stringify(dataToStore));
        } catch (silentError) {
          // Clear the cached account if token acquisition fails
          msalInstance.getAllAccounts().forEach(acc => {
            msalInstance.setActiveAccount(null);
          });
        }
      }
    }
  } catch (error) {
    // Error handling redirect promise
  }
  
  root.render(
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <StylesProvider jss={jss}>
          <React.StrictMode>
            <BrowserRouter>
              <StandaloneModeProvider>
                <CssBaseline />
                <GlobalLoader />
                <Router />
              </StandaloneModeProvider>
            </BrowserRouter>
          </React.StrictMode>
        </StylesProvider>
      </ThemeProvider>
    </MsalProvider>
  );
});

reportWebVitals();
